import { HTMLAttributes, useEffect, useMemo, useState } from 'react'

import { Text } from '@hhgtech/hhg-components'
import { Popover } from '@mantine/core'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { PREGNANCY_CATEGORY } from 'interfaces/constants/pregenancyCategory'

import { StyledSlider, StyledTrimester } from './index.styled'

type TrimeterProps = {
  currentWeek: number
} & HTMLAttributes<HTMLDivElement>
export const Trimeter = ({
  currentWeek: _currentWeek,
  ...rest
}: TrimeterProps) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const [currentWeek, setCurrentWeek] = useState(_currentWeek + 1)
  useEffect(() => {
    setCurrentWeek(_currentWeek + 1)
  }, [_currentWeek])

  const remainingWeeks = 40 - currentWeek
  const currentTrimeter = useMemo(() => {
    if (currentWeek < 14) {
      return 0
    } else if (currentWeek >= 27) {
      return 2
    }
    return 1
  }, [currentWeek])

  const categoryLink = PREGNANCY_CATEGORY[locale][currentTrimeter]
  const renderContent = () => {
    if (remainingWeeks === 1)
      return f({
        id: 'dueDateTool.weekToGo.one',
        defaultMessage: '1 week to go',
      })
    if (remainingWeeks <= 0)
      return f({
        id: 'dueDateTool.weekToGo.outOfRange',
        defaultMessage: 'Anytime now',
      })

    return f(
      {
        id: 'dueDateTool.weekToGo',
        defaultMessage: '{week} weeks to go',
      },
      {
        week: remainingWeeks,
      },
    )
  }

  const renderTrimeter = () => {
    switch (currentTrimeter) {
      case 0:
        return f({
          id: 'dueDatePage.firstTrimester',
          defaultMessage: '1st Trimester',
        })
      case 1:
        return f({
          id: 'dueDatePage.secondTrimester',
          defaultMessage: '2nd Trimester',
        })
      case 2:
        return f({
          id: 'dueDatePage.thirdTrimester',
          defaultMessage: '3rd Trimester',
        })
      default:
        return ''
    }
  }

  return (
    <StyledTrimester {...rest}>
      <Popover
        opened={currentWeek >= 4}
        position="top"
        withArrow
        shadow="md"
        arrowOffset={10}
      >
        <StyledSlider
          thumbChildren={
            <Popover.Target>
              <div key={currentWeek} />
            </Popover.Target>
          }
          min={1}
          max={40}
          value={currentWeek}
          thumbSize={20}
          size="lg"
          showLabelOnHover={false}
          marks={[{ value: 13 }, { value: 26 }]}
          // isStatic
          labelAlwaysOn={false}
        />
        <Popover.Dropdown>
          <div>
            <Link href={categoryLink} prefetch={false}>
              <Text size="n1" type="semiBold">
                {renderTrimeter()}
              </Text>
            </Link>
            {currentWeek !== 40 && !isMobile && (
              <Text size="n1">{renderContent()}</Text>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>
    </StyledTrimester>
  )
}
